<script setup lang="ts">
  import type { Brand, ProductCategory } from '@storefront/types';
  import type { ProductVariantResource, ProductResource, ProductFlexibleResource } from '@storefront/resources';
  import { Features } from '@shared/features';
  import { ProductType } from '@othercommerce/domain';
  import ShopProductViewConfigurator from './ShopProductViewConfigurator.vue';
  import { computed } from 'vue';

  interface Props {
    product: ProductResource | ProductFlexibleResource,
    variant: ProductVariantResource;
    brand?: Brand | null;
    category?: ProductCategory | null;
  }

  const props = defineProps<Props>();

  const title = computed(() => {
    return props.product.type === ProductType.VARIANT ? props.variant.titleSeo || props.variant.title : props.product.titleSeo || props.product.title;
  });
</script>
<template>
  <div class="details container" :class="{'details-configurator': product.type === ProductType.FLEXIBLE}">
    <div class="gallery">
      <SfProductGallery :photos="variant.photos" :variant="variant" />
    </div>
    <div class="content">
      <header class="header">
        <ShopProductViewTags icon :variant="variant" />
        <h1 v-text="title" />
        <SfStars :count="variant.reviews.count" :rating="variant.reviews.average" />
      </header>

      <template v-if="product.type === ProductType.FLEXIBLE">
        <template v-if="variant.descriptionShort">
          <div class="description" v-html="variant.descriptionShort" />
        </template>
        <div class="details-configurator-content">
          <ShopProductViewConfigurator :product="product as ProductFlexibleResource" :variant="variant" />
        </div>
      </template>

      <template v-else>
        <div class="wrapper">
          <div class="left">
            <template v-if="variant.descriptionShort">
              <div class="description" v-html="variant.descriptionShort" />
            </template>
            <div class="description" v-if="variant.isMedicinalProduct && Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled">
              {{ $t('SHOP.PRODUCT.MESSAGE.MEDICINAL_PRODUCT_NOTICE') }}
            </div>
            <ShopProductViewComponents :variant="variant" />
            <ShopProductViewMeta :brand="brand" :category="category" :variant="variant" />
            <ShopProductViewActions :variant="variant" />
          </div>
          <div class="right">
            <ShopProductViewPicker :variant="variant" />
          </div>
        </div>
      </template>

      <ShopProductViewFiles :variant="variant" />
    </div>
  </div>
</template>
<style lang="scss">
  :root {
    --sf-product-gallery-view-details-button-width: 3rem;
    --sf-product-gallery-view-details-button-height: 3rem;
    --sf-product-gallery-view-details-button-space: 0;
    --sf-product-gallery-view-details-button-icon-size: 1.5rem;
  }

  .product-gallery .product-gallery__images {
    --sf-carousel-button-width: var(--sf-product-gallery-view-details-button-width);
    --sf-carousel-button-height: var(--sf-product-gallery-view-details-button-height);
    --sf-carousel-button-space: var(--sf-product-gallery-view-details-button-space);
    --sf-carousel-button-icon-size: var(--sf-product-gallery-view-details-button-icon-size);
  }
</style>
<style scoped lang="scss">
  .details {
    display: flex;
    gap: 2rem;
    margin-bottom: var(--margin-default);
    padding: 1.5rem;
    background: var(--background-card);
    border-radius: var(--border-radius-card);
    border: var(--border-card);

    .gallery {
      height: fit-content;
      width: 40%;
    }

    .content {
      display: flex;
      flex-direction: column;
      width: 60%;
      gap: 2rem;

      .header {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        h1 {
          font-weight: var(--font-weight-bold);
          font-size: 1.5rem;
        }
      }

      .wrapper {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;

        .left {
          display: flex;
          flex-direction: column;
          flex: 1 1 50%;
          gap: 1rem;

          .description {
            font-size: 0.875rem;
            --font-size: 0.875rem;
          }
        }
      }
    }

    &.details-configurator {
      .gallery {
        width: 30%;
      }

      .content {
        width: 70%;
      }

      .details-configurator-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

    }
  }

  @media (max-width: 1200px) {
    .details {
      .gallery {
        width: 45%;
      }

      .content {
        width: 55%;

        .wrapper {
          grid-template-columns: 1fr
        }
      }


      &.details-configurator {
        flex-direction: column;

        .gallery {
          width: 100%;
        }

        .content {
          width: 100%;
        }

        .details-configurator-content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

      }
    }
  }

  @media (max-width: 992px) {
    .details {
      flex-direction: column;
      padding: 1rem 0.625rem;

      .gallery {
        width: 100%;
      }

      .content {
        width: 100%;
        gap: 1rem;

        .header {
          h1 {
            font-size: 1.25rem;
          }
        }

        .wrapper {
          gap: 1rem;
        }
      }
    }
  }
</style>
